import React, { useState, useEffect, useRef } from 'react';
import cornerstone from 'cornerstone-core';
import cornerstoneTools from 'cornerstone-tools';
import { useTranslation } from 'react-i18next';
import {
  FaCopy,
  FaAngleDoubleDown,
  FaLink,
  FaChevronRight,
  FaChevronLeft,
  FaEye,
  FaEyeSlash
} from '../../../../util/reactIcons.js';
import {
  ViewportOverlay,
  TopLeft,
  TopRight,
  BottomLeft,
  BottomRight,
  Icon,
  Tooltip,
} from './StyledCustomOverlay.js';
import {
  formatNumberPrecision,
  getCompression,
  formatPN,
  getFormattedStudyDate,
  formatTM,
  isValidNumber,
} from '../../../../util/CustomOverlayUtils';
import store from '../../../../store/store';
import {
  setScrollType,
  setLayoutElement,
} from '../../../../actions/LayoutActions';
import { sync } from '../sync';
import { getEnabledElement } from '../../../../util/state';

export const CustomOverlay = ({ imageId, ...props }) => (
  imageId ? <CustomOverlayContent {...props} imageId={imageId} /> : null
);

const CustomOverlayContent = ({
  scale,
  windowWidth,
  windowCenter,
  imageId,
  imageIndex,
  stackSize,
}) => {
  const seriesMetadata = cornerstone.metaData.get('generalSeriesModule', imageId) || '';
  const { seriesNumber, seriesDescription, seriesInstanceUID } = seriesMetadata;

  const state = store.getState((st) => st);
  const { isContinuousScroll, elements } = state.layout;

  // fetch the viewport index via SeriesInstanceUID

  let viewIndex = elements.findIndex(
    (element) => element && element.SeriesInstanceUID === seriesInstanceUID,
  );
  const layoutElement = viewIndex >= 0 ? elements[viewIndex] : elements[0];

  const offset = 0.8;
  const min = 0.3;
  const max = 1;

  const c_element = getEnabledElement(viewIndex);
  const cornerstoneCanvas = c_element && c_element.querySelector('.cornerstone-canvas');
  const viewportScale = cornerstoneCanvas
    ? cornerstoneCanvas.width / cornerstoneCanvas.height
    : 1;

  const [fontSize, setFontSize] = useState(
    between(viewportScale * offset, min, max),
  );
  const [pixelInfo, setPixelInfo] = useState(null);
  const lastPixelInfo = useRef(null);
  const lastImagePoint = useRef(null);
  const currentImageId = useRef(imageId);
  currentImageId.current = imageId;

  const { WITH_PATIENT_DISCLOSED, SHOW_SOPINSTANCEUID } = state.config;

  const zoomPercentage = formatNumberPrecision(scale * 100, 0);
  const imagePlaneModule = cornerstone.metaData.get('imagePlaneModule', imageId) || '';
  const SOPInstanceUID = cornerstone.metaData.get('SOPInstanceUID', imageId) || '';
  const { rows, columns, sliceThickness, sliceLocation, imagePositionPatient } = imagePlaneModule;

  const generalStudyModule = cornerstone.metaData.get('generalStudyModule', imageId) || {};
  const { studyDate, studyTime, studyDescription } = generalStudyModule;

  const patientModule = (WITH_PATIENT_DISCLOSED && cornerstone.metaData.get('patientModule', imageId)) || {};
  const { patientId, patientName } = patientModule;
  const cineModule = cornerstone.metaData.get('cineModule', imageId) || {};
  const { frameTime } = cineModule;
  const frameRate = formatNumberPrecision(1000 / frameTime, 1);
  const compression = getCompression(imageId);
  const wwwc = `W: ${windowWidth.toFixed(0)} L: ${windowCenter.toFixed(0)}`;
  const imageDimensions = `${columns} x ${rows}`;
  const { t } = useTranslation();
  const copyID = t('tooltip.copySOPInstanceUID');
  const copySuccess = t('tooltip.copySuccess');
  const notSecure = t('tooltip.notSecure');
  const continuousScrollTooltip = t('tooltip.continuousScroll');
  const synchedFramesTooltip = t('tooltip.isSynchedFrames');
  const [tooltipText, setTooltipText] = useState(SOPInstanceUID);
  const [showSOPTooltip, setShowSOPTooltip] = useState(true);

  useEffect(() => {
    setFontSize(between(viewportScale * offset, min, max));
  }, [viewportScale]);

  useEffect(() => {
    if (!c_element) return undefined;

    const clearPixelInfo = () => {
      lastPixelInfo.current = null;
      setPixelInfo(null);
    };

    const updatePixelInfo = (imagePoint, image) => {
      if (!imagePoint || !image || !Number.isFinite(imagePoint.x) || !Number.isFinite(imagePoint.y)) return;

      const column = Math.round(imagePoint.x);
      const row = Math.round(imagePoint.y);
      if (column < 0 || row < 0 || column >= image.columns || row >= image.rows) {
        clearPixelInfo();
        return;
      }

      const currentPixelInfo = { column, row };
      if (!image.color) {
        const storedPixel = cornerstone.getStoredPixels(c_element, column, row, 1, 1)[0];
        const slope = Number.isFinite(image.slope) ? image.slope : 1;
        const intercept = Number.isFinite(image.intercept) ? image.intercept : 0;
        const modalityValue = storedPixel * slope + intercept;
        const imageMetadataId = image.imageId
          || cornerstone.getEnabledElement(c_element)?.image?.imageId
          || currentImageId.current;
        const modalityValueType = cornerstone.metaData
          .get('generalSeriesModule', imageMetadataId)?.modality;
        const rescaleValueType = image.rescaleType
          || cornerstone.metaData.get('modalityLutModule', imageMetadataId)?.rescaleType;
        const modality = typeof modalityValueType === 'string'
          ? modalityValueType.trim().toUpperCase()
          : '';
        const rescaleType = typeof rescaleValueType === 'string'
          ? rescaleValueType.trim().toUpperCase()
          : '';
        const isHounsfieldValue = modality === 'CT' || rescaleType === 'HU';

        currentPixelInfo.value = isHounsfieldValue
          ? String(Number(modalityValue.toFixed(2)))
          : String(storedPixel);
        currentPixelInfo.unit = 'HU';
      }

      const previous = lastPixelInfo.current;
      if (previous
        && previous.column === currentPixelInfo.column
        && previous.row === currentPixelInfo.row
        && previous.value === currentPixelInfo.value
        && previous.unit === currentPixelInfo.unit) return;

      lastPixelInfo.current = currentPixelInfo;
      setPixelInfo(currentPixelInfo);
    };

    const handleMouseMove = (event) => {
      const eventData = event.detail || {};
      const imagePoint = eventData.currentPoints && eventData.currentPoints.image;
      if (!imagePoint) return;

      lastImagePoint.current = { x: imagePoint.x, y: imagePoint.y };
      const image = eventData.image || cornerstone.getEnabledElement(c_element)?.image;
      updatePixelInfo(lastImagePoint.current, image);
    };

    const handleNewImage = (event) => {
      if (!lastImagePoint.current) return;

      const image = event.detail?.image || cornerstone.getEnabledElement(c_element)?.image;
      updatePixelInfo(lastImagePoint.current, image);
    };

    const handleMouseLeave = () => {
      lastImagePoint.current = null;
      clearPixelInfo();
    };

    clearPixelInfo();
    lastImagePoint.current = null;
    c_element.addEventListener(cornerstoneTools.EVENTS.MOUSE_MOVE, handleMouseMove);
    c_element.addEventListener('cornerstonenewimage', handleNewImage);
    c_element.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      c_element.removeEventListener(cornerstoneTools.EVENTS.MOUSE_MOVE, handleMouseMove);
      c_element.removeEventListener('cornerstonenewimage', handleNewImage);
      c_element.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [c_element]);

  const toClipboard = (e, id) => {
    e.stopPropagation();

    if (window.isSecureContext) {
      navigator.clipboard
        .writeText(id)
        .then(() => {
          setTooltipText(copySuccess);
          setTimeout(() => setTooltipText(SOPInstanceUID), 2000);
        })
        .catch((err) => {
          console.error('Something went wrong', err);
        });
    } else {
      setTooltipText(notSecure);
      setTimeout(() => setTooltipText(SOPInstanceUID), 2000);
    }
  };

  const normal = (
  <>
    <TopLeft>
      <div>{formatPN(patientName)}</div>
      <div>{patientId}</div>
    </TopLeft>

    <TopRight>
      <div>{studyDescription}</div>
      {studyDate && <div>{`${t('overlay.StudyDate')}: ${getFormattedStudyDate(studyDate)}`}</div>}
      {formatTM(studyTime) && <div>{`${t('overlay.StudyTime')}: ${formatTM(studyTime)}`}</div>}
      {window.isSecureContext && SHOW_SOPINSTANCEUID && (
      <Icon>
        <FaCopy
          onMouseOver={(e) => {
              e.stopPropagation();
              setTooltipText(copyID);
            }}
          onMouseLeave={(e) => {
              e.stopPropagation();
              tooltipText !== copySuccess
              ? setTooltipText(SOPInstanceUID)
              : setTimeout(() => setTooltipText(SOPInstanceUID), 2000);
            }}
          onClick={(e) => toClipboard(e, SOPInstanceUID)}
        />
        {showSOPTooltip ? (
          <FaEyeSlash
            style={{ marginRight: 6, cursor: 'pointer' }}
            title="Ocultar SOPInstanceUID"
            onClick={(e) => {
              e.stopPropagation();
              setShowSOPTooltip(false);
              }}
          />
            ) : (
              <FaEye
                style={{ marginRight: 6, cursor: 'pointer' }}
                title="Mostrar SOPInstanceUID"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSOPTooltip(true);
                  }}
              />
            )}
        {showSOPTooltip && (
        <Tooltip>
          {tooltipText === copySuccess || tooltipText === copyID
              ? tooltipText
              : SOPInstanceUID}
        </Tooltip>
            )}
      </Icon>
          )}

      {layoutElement && layoutElement.isSynchedFrames === true && (
      <>
        <div>
          {imagePositionPatient
                && imagePositionPatient[0]
                && `x:${imagePositionPatient[0]}`}
        </div>
        <div>
          {imagePositionPatient
                && imagePositionPatient[1]
                && `y:${imagePositionPatient[1]}`}
        </div>
        <div>
          {imagePositionPatient
                && imagePositionPatient[2]
                && `z:${imagePositionPatient[2]}`}
        </div>
      </>
        )}
    </TopRight>

    <BottomRight>
      {/* synchronizations */}
      <Icon
        className={layoutElement.isSynchedFrames === true ? 'selected' : ''}
      >
        <FaLink
          onMouseOver={(e) => {
              e.stopPropagation();
            }}
          onClick={() => {
              store.dispatch(
                setLayoutElement(viewIndex, {
                  ...elements[viewIndex],
                  isSynchedFrames: !elements[viewIndex].isSynchedFrames,
                }),
              );
              sync();
            }}
        />
        <Tooltip
          className={layoutElement.isSynchedFrames === true ? 'selected' : ''}
        >
          {layoutElement.isSynchedFrames === true && synchedFramesTooltip}
        </Tooltip>
      </Icon>
      {/* continuous scroll */}
      <Icon className={isContinuousScroll === true ? 'selected' : ''}>
        <FaAngleDoubleDown
          onMouseOver={(e) => {
              e.stopPropagation();
            }}
          onClick={() => store.dispatch(setScrollType())}
        />
        <Tooltip className={isContinuousScroll === true ? 'selected' : ''}>
          {isContinuousScroll && continuousScrollTooltip}
        </Tooltip>
      </Icon>
      <div>
        Zoom:
        {zoomPercentage}
        %
      </div>
      <div>{wwwc}</div>
      <div className="compressionIndicator">{compression}</div>
    </BottomRight>

    <BottomLeft>
      {pixelInfo && (
      <div>
        <div>
          <b>Col: </b>
          {pixelInfo.column}
          {' '}
          <b>Row: </b>
          {pixelInfo.row}
        </div>
        {pixelInfo.unit && (
        <div>
          <b>
            {pixelInfo.unit}
            :
            {' '}
          </b>
          {pixelInfo.value}
        </div>
)}
      </div>
        )}
      <div>{seriesNumber >= 0 ? `Ser: ${seriesNumber}` : ''}</div>
      <div>{stackSize > 1 ? `Img: ${imageIndex}/${stackSize}` : ''}</div>
      <div>
        {frameRate >= 0 ? `${formatNumberPrecision(frameRate, 2)} FPS` : ''}
        <div>{imageDimensions}</div>
        <div>
          {isValidNumber(sliceLocation)
              ? `Loc: ${formatNumberPrecision(sliceLocation, 2)} mm `
              : ''}
          {sliceThickness
              ? `Thick: ${formatNumberPrecision(sliceThickness, 2)} mm`
              : ''}
        </div>
        <div>{seriesDescription}</div>
      </div>
    </BottomLeft>
  </>
  );

  return (
    <ViewportOverlay id="customOverlay" size={fontSize}>
      {normal}
    </ViewportOverlay>
  );
};

const between = (size, min, max) => Math.min(max, Math.max(min, size));
